/**
 * DSCR API — DSCR "Quick Price" sizer pricing + rate-lock endpoints.
 *
 * Ported from digital_sizer (frontend/src/dscr/api.ts + lockApi.ts). Now wired
 * onto the LOS client (`dsHttp` → eastview-los), per the digital-sizer base-URL
 * removal.
 *
 * QUICK PRICE IS LIVE (2026-08-14): `POST /pricing/scenario/dscr_quick`
 * (pricing-scenario-sizers.md). It replaced the digital-sizer `/dscr/quick-price`,
 * which this app never had a backend for.
 *
 * ⚠️ Still TBD: the `/dscr/*` LOCK routes below (lock-policies, locks). The LOS
 * has a loan-scoped `/request_dscr_lock/{dealId}` — a different workflow — but no
 * standalone sizer lock endpoints.
 *
 * The correspondent list moved to `sizersShared/useSizerCorrespondents` — all
 * three sizers need the same picker, and it belongs next to them rather than in
 * the DSCR-specific module.
 */
import { dsHttp } from "../lib/digitalSizerHttp";

export const dscrApi = {
  // POST /pricing/scenario/dscr_quick → { inputs, result, bestExecution,
  // breakeven, ladder, bandBests, outcomeMatrix, marketData, ruralCheck,
  // closingCostLadder, scenarioId, scenarioVersion }.
  //
  // `result` IS THE ANCHORED ANSWER, and `result.anchor` says to what:
  // 'requested' when `requestedCoupon` was sent (how THAT rate prices — the
  // headline flow), else 'best' = best execution (highest PREMIUM, ties to the
  // lowest coupon), else 'ladder_mid' when nothing passed. `breakeven` is the
  // thinnest passing rung — informational only, never the headline.
  //
  // The old `sensitivity` block is GONE (backend 2026-08-18): `outcomeMatrix`
  // replaced it — anchor ±2 rungs × loan ±2×2.5%, every cell a FULL reprice,
  // rows capped at the guideline max LTV and flagged. There is no `bands`
  // object any more; per-cell `passing` is the verdict.
  //
  // ADDRESS IS REQUIRED: at minimum `address.zip`, or the endpoint 422s —
  // market data and the CFPB rural determination embed into the price.
  //
  // PERSISTED (2026-08-17): every priced run is stored as a
  // `pricing_scenarios` doc with an append-only version history. Pass
  // `scenarioId` back in the body to reprice THE SAME scenario as its next
  // version — omit it and each reprice orphans a new v1, losing the drift
  // record between the first quote and the last.
  //
  // 422 IS NOT AN ERROR — TWO GATES SHARE THE STATUS:
  //   • `{missingRequirements: [...]}` — REQUIRED_BY_LLPA inputs, in two entry
  //     shapes: `{llpa, missing[], note}` (a sheet table that would be skipped)
  //     and `{field, reason}` (a single input, e.g. `address.zip`). The caller
  //     renders both on the named fields.
  //   • `{ruralGate: {ruralCheck, marketData, reason, proceed}}` — a CFPB rural
  //     determination. Pricing resumes only when the SAME payload is re-POSTed
  //     with `ruralExceptionRequested: true` + a `ruralExceptionNote`. Never
  //     auto-set that flag; it is a user action and it persists on the scenario.
  // Only a non-422 failure is a failure.
  //
  // Everything — DSCR, PITIA, every price and every outcome-matrix cell — is
  // computed server-side; never recompute a cell here (pricing-scenario-sizers.md
  // "Shipping requirements": render-only, recompute = re-POST). Premium is in
  // BOND POINTS (`premiumDiscountPts`) and is never rendered as a cash credit.
  quickPrice: (inputs) =>
    dsHttp.post("/pricing/scenario/dscr_quick", inputs).then((r) => r.data),
};

// ── Rate-lock workflow (ported from dscr/lockApi.ts) ─────────────────────────
// ⚠️ TBD: none of these `/dscr/*` lock routes exist on eastview-os yet — every
// call 404s until the backend adds them.
export const lockApi = {
  // TBD (no eastview-os route): GET /dscr/lock-policies/:correspondentId
  getPolicy: (correspondentId) =>
    dsHttp.get(`/dscr/lock-policies/${correspondentId}`).then((r) => r.data),

  // TBD (no eastview-os route): GET /dscr/locks?deal_id=...
  list: (dealId) =>
    dsHttp
      .get(
        `/dscr/locks${dealId ? `?deal_id=${encodeURIComponent(dealId)}` : ""}`,
      )
      .then((r) => r.data),

  // TBD (no eastview-os route): POST /dscr/locks
  create: (body) => dsHttp.post("/dscr/locks", body).then((r) => r.data),

  // TBD (no eastview-os route): POST /dscr/locks/:lockId/extend
  extend: (lockId, daysExtended, note) =>
    dsHttp
      .post(`/dscr/locks/${lockId}/extend`, {
        days_extended: daysExtended,
        note,
      })
      .then((r) => r.data),

  // TBD (no eastview-os route): POST /dscr/locks/:lockId/reprice
  reprice: (lockId) =>
    dsHttp.post(`/dscr/locks/${lockId}/reprice`).then((r) => r.data),

  // TBD (no eastview-os route): POST /dscr/locks/:lockId/void
  void: (lockId, reason) =>
    dsHttp.post(`/dscr/locks/${lockId}/void`, { reason }).then((r) => r.data),
};

export default dscrApi;
